import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Collapse,
  Tabs,
  Tab,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { Merchant, MerchantBalance, MerchantFormData, RoleEnum, User } from '../../types/index';
import { merchantsService, authService, usersService } from '../../services';
import { useNotification } from '../../contexts/NotificationContext';
import MerchantRates from './components/MerchantRates';
import MerchantBalances from './components/MerchantBalances';
import AnimatedPage from '../../components/AnimatedPage';
import { useMerchants } from './hooks/useMerchants';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { canPerformAction, ActionKey } from '../../auth/actionMatrix';
import { hasPermission, Permission } from '../../auth/permissions';
import { CopyableText } from '@/utils/copy';
import TelegramChatsSection from '@/components/TelegramChatsSection';
import type { TelegramChat } from '@/types/common/telegram';
import SearchableSelect from '@/components/SearchableSelect';
import StatisticDashboard from '@/pages/statistics/StatisticDashboard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

interface MerchantBalancesRef {
  handleOpenDialog: () => void;
}

interface MerchantRatesRef {
  handleOpenDialog: () => void;
}

const Merchants: React.FC = () => {
  const { merchants, loading, error, refetch } = useMerchants();
  const [users, setUsers] = useState<User[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [telegramChats, setTelegramChats] = useState<TelegramChat[]>([]);
  const [telegramChatsLoading, setTelegramChatsLoading] = useState(false);
  const [telegramChatsError, setTelegramChatsError] = useState<string | null>(null);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [formData, setFormData] = useState<MerchantFormData>({
    name: '',
    user_id: '',
    public_key: '',
    api_key: '',
  });
  const [tabValue, setTabValue] = useState(0);
  const [merchantSearch, setMerchantSearch] = useState('');
  const [merchantUserFilter, setMerchantUserFilter] = useState('');
  const { showNotification } = useNotification();
  const balancesRef = useRef<MerchantBalancesRef>(null);
  const ratesRef = useRef<MerchantRatesRef>(null);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const canCreateMerchant = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANTS_CREATE),
    [currentUser],
  );
  const canUpdateMerchant = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANTS_UPDATE),
    [currentUser],
  );
  const canDeleteMerchant = useMemo(
    () => canPerformAction(currentUser, ActionKey.MERCHANTS_DELETE),
    [currentUser],
  );
  const canViewMerchantStatistics = Boolean(
    currentUser && (
      currentUser.is_superuser
      || currentUser.role === RoleEnum.CLIENT_MANAGER
      || hasPermission(currentUser, Permission.MERCHANTS_READ)
      || hasPermission(currentUser, Permission.MERCHANTS_MANAGE)
    )
  );
  const filteredMerchants = useMemo(() => {
    const query = merchantSearch.trim().toLowerCase();
    return merchants.filter((merchant) => {
      const user = users.find((item) => item.id === merchant.user_id);
      const matchesSearch = !query || `${merchant.name} ${merchant.public_key} ${merchant.api_key} ${user?.username ?? ''} ${user?.email ?? ''}`.toLowerCase().includes(query);
      return matchesSearch && (!merchantUserFilter || merchant.user_id === merchantUserFilter);
    });
  }, [merchantSearch, merchantUserFilter, merchants, users]);

  const fetchUsers = async () => {
    try {
      const usersData = await usersService.getUsers();
      setUsers(usersData);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      showNotification('Ошибка при загрузке пользователей', 'error');
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const userData = await authService.me();
      setCurrentUserId(userData.id);
      setFormData(prev => ({ ...prev, user_id: userData.id }));
    } catch (err: any) {
      console.error('Error fetching current user:', err);
      showNotification('Ошибка при получении данных пользователя', 'error');
    }
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
  }, []);

  const handleOpenDialog = (merchant?: Merchant) => {
    setTelegramChats([]);
    setTelegramChatsError(null);
    if (merchant) {
      if (!canUpdateMerchant) {
        showNotification('У вас нет прав для редактирования мерчантов', 'error');
        return;
      }
    } else if (!canCreateMerchant) {
      showNotification('У вас нет прав для создания мерчантов', 'error');
      return;
    }
    if (merchant) {
      setSelectedMerchant(merchant);
      setFormData({
        name: merchant.name,
        user_id: merchant.user_id,
        public_key: merchant.public_key,
        api_key: merchant.api_key,
      });
      setTelegramChatsLoading(true);
      merchantsService.getTelegramChats(merchant.id)
        .then(setTelegramChats)
        .catch(() => setTelegramChatsError('Не удалось загрузить Telegram-группы'))
        .finally(() => setTelegramChatsLoading(false));
    } else {
      setTelegramChatsLoading(false);
      setSelectedMerchant(null);
      setFormData({
        name: '',
        user_id: currentUserId,
        public_key: generateKey(),
        api_key: generateKey(),
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMerchant(null);
    setFormData({
      name: '',
      user_id: currentUserId,
      public_key: '',
      api_key: '',
    });
  };

  const handleGenerateKeys = () => {
    setFormData({
      ...formData,
      public_key: generateKey(),
      api_key: generateKey(),
    });
  };

  const handleChange = (field: keyof MerchantFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: MerchantFormData) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (field: 'user_id') => (e: SelectChangeEvent) => {
    setFormData((prev: MerchantFormData) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedMerchant) {
        if (!canUpdateMerchant) {
          showNotification('У вас нет прав для редактирования мерчантов', 'error');
          return;
        }
        const { user_id: _userId, ...updateData } = formData;
        await merchantsService.updateMerchant(selectedMerchant.id, updateData);
        showNotification('Мерчант успешно обновлен', 'success');
      } else {
        if (!canCreateMerchant) {
          showNotification('У вас нет прав для создания мерчантов', 'error');
          return;
        }
        await merchantsService.createMerchant(formData);
        showNotification('Мерчант успешно создан', 'success');
      }
      refetch();
      handleCloseDialog();
    } catch (err: any) {
      showNotification(err.message || 'Ошибка при сохранении мерчанта', 'error');
    }
  };

  const handleDelete = async () => {
    if (!selectedMerchant) return;
    try {
      if (!canDeleteMerchant) {
        showNotification('У вас нет прав для удаления мерчантов', 'error');
        return;
      }
      await merchantsService.deleteMerchant(selectedMerchant.id);
      showNotification('Мерчант успешно удален', 'success');
      refetch();
      setDeleteDialogOpen(false);
      setSelectedMerchant(null);
    } catch (err: any) {
      showNotification(err.message || 'Ошибка при удалении мерчанта', 'error');
    }
  };

  const generateKey = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <AnimatedPage>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Мерчанты
          </Typography>
          {tabValue === 0 && canCreateMerchant && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={!canCreateMerchant}
            >
              Добавить мерчант
            </Button>
          )}
          {tabValue === 1 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => balancesRef.current?.handleOpenDialog()}
            >
              Добавить баланс
            </Button>
          )}
          {tabValue === 2 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => ratesRef.current?.handleOpenDialog()}
            >
              Добавить ставку
            </Button>
          )}
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Мерчанты" />
            <Tab label="Балансы" />
            <Tab label="Ставки" />
            {canViewMerchantStatistics && <Tab label="Статистика" />}
          </Tabs>
        </Paper>

        <TabPanel value={tabValue} index={0}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '2fr 1fr auto' }} gap={2}>
              <TextField label="Название, пользователь или ключ" value={merchantSearch} onChange={(event) => setMerchantSearch(event.target.value)} />
              <SearchableSelect label="Пользователь" value={merchantUserFilter} options={users.map((item) => ({ id: item.id, label: `${item.username}${item.email ? ` · ${item.email}` : ''}` }))} onChange={setMerchantUserFilter} />
              <Button variant="outlined" onClick={() => { setMerchantSearch(''); setMerchantUserFilter(''); }}>Сбросить</Button>
            </Box>
          </Paper>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Название</TableCell>
                    <TableCell>Пользователь</TableCell>
                    <TableCell>Public Key</TableCell>
                    <TableCell>API Key</TableCell>
                    <TableCell>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMerchants.map((merchant) => (
                    <TableRow key={merchant.id}>
                      <TableCell>{merchant.name}</TableCell>
                      <TableCell>
                        {users.find(u => u.id === merchant.user_id)?.username || merchant.user_id}
                      </TableCell>
                      <TableCell>
                        <CopyableText
                          value={merchant.public_key}
                          copiedMessage="Public Key скопирован"
                          typographySx={{
                            mr: 1,
                            maxWidth: '220px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <CopyableText
                          value={merchant.api_key}
                          copiedMessage="API Key скопирован"
                          typographySx={{
                            mr: 1,
                            maxWidth: '220px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title={canUpdateMerchant ? 'Редактировать' : 'Недостаточно прав'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (!canUpdateMerchant) {
                                    showNotification('У вас нет прав для редактирования мерчантов', 'error');
                                    return;
                                  }
                                  handleOpenDialog(merchant);
                                }}
                                disabled={!canUpdateMerchant}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={canDeleteMerchant ? 'Удалить' : 'Недостаточно прав'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (!canDeleteMerchant) {
                                    showNotification('У вас нет прав для удаления мерчантов', 'error');
                                    return;
                                  }
                                  setSelectedMerchant(merchant);
                                  setDeleteDialogOpen(true);
                                }}
                                color="error"
                                disabled={!canDeleteMerchant}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <MerchantBalances ref={balancesRef} />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <MerchantRates ref={ratesRef} />
        </TabPanel>

        {canViewMerchantStatistics && (
          <TabPanel value={tabValue} index={3}>
            <StatisticDashboard type="merchants" />
          </TabPanel>
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedMerchant ? 'Редактировать мерчанта' : 'Добавить мерчанта'}
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Название"
                value={formData.name}
                onChange={handleChange('name')}
                margin="normal"
                required
              />

              <FormControl fullWidth margin="normal" disabled={Boolean(selectedMerchant)}>
                <InputLabel>Пользователь</InputLabel>
                <Select
                  value={formData.user_id}
                  onChange={handleSelectChange('user_id')}
                  label="Пользователь"
                  required
                >
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Public Key"
                value={formData.public_key}
                onChange={handleChange('public_key')}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <Button onClick={handleGenerateKeys}>
                      Сгенерировать
                    </Button>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="API Key"
                value={formData.api_key}
                onChange={handleChange('api_key')}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <Button onClick={handleGenerateKeys}>
                      Сгенерировать
                    </Button>
                  ),
                }}
              />

              {selectedMerchant && (
                <TelegramChatsSection
                  chats={telegramChats}
                  loading={telegramChatsLoading}
                  error={telegramChatsError}
                  ownerType="MERCHANT"
                  onCreate={canUpdateMerchant ? (payload) => merchantsService.createTelegramChat(selectedMerchant.id, payload) : undefined}
                  onEdit={canUpdateMerchant ? (chatId, payload) => merchantsService.updateTelegramChat(selectedMerchant.id, chatId, payload) : undefined}
                  onChanged={(updated) => setTelegramChats((items) => items.some((item) => item.id === updated.id) ? items.map((item) => item.id === updated.id ? updated : item) : [...items, updated])}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button onClick={handleSubmit} variant="contained">
              {selectedMerchant ? 'Сохранить' : 'Добавить'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setSelectedMerchant(null);
          }}
        >
          <DialogTitle>Подтверждение удаления</DialogTitle>
          <DialogContent>
            <Typography>
              Вы уверены, что хотите удалить мерчанта "{selectedMerchant?.name}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setSelectedMerchant(null);
              }}
            >
              Отмена
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Удалить
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AnimatedPage>
  );
};

export default Merchants;
